import React, { useState, useEffect } from 'react';
import ModernJobItem from './ModernJobItem';
import { ListChecks, Inbox, Activity, Sparkles } from 'lucide-react';
import { useSocket } from '../context/SocketContext'; // Import useSocket

// JobItem'da kullanılan ve backend'den gelebilecek tüm alanları içeren Job arayüzü
interface Job {
    id: string;
    vodUrl: string;
    status: string;
    chatHistory?: any;
    llmOutputHighlights?: { start: number; end: number; reason?: string; startTime?: number; endTime?: number; explanation?: string }[];
    createdAt: string;
    updatedAt: string;
    clips: LocalClip[]; // LocalClip[] olarak güncellendi
    whisperModel?: string;
    whisperDevice?: string;
    llmCost?: number;
    fullVodSubtitlePath?: string;
    redetectionCount?: number;
    downloadProgress?: number;
    isDownloadIndeterminate?: boolean;
    // downloadStatusText?: string; // JobItem'da UI'dan kaldırıldığı belirtilmişti
    subtitleProgress?: number;
    // processingProgress?: number; // İhtiyaç duyulursa eklenebilir
    currentStageDetail?: string;
    clipsProcessedCount?: number;
    clipsTotalCount?: number;
    
    // VOD metadata (modern UI için)
    vodTitle?: string;
    vodThumbnailUrl?: string;
}

// Local Clip interface that extends shared Clip with additional fields
interface LocalClip {
    id: string;
    startTime: number;
    endTime: number;
    filePath?: string;
    fileName?: string;
    reason?: string;
    explanation?: string;
    // Shared Clip fields
    title: string;
    description: string;
    thumbnailUrl?: string;
    videoUrl: string;
    clipUrl?: string;
    subtitleUrl?: string;
    jobId: string;
    vodId: string;
    duration: number;
    createdAt: string;
    updatedAt: string;
}

interface JobQueueListProps {
    jobs: Job[] | null;
    loading: boolean;
    error: string | null;
    onCancelJob: (jobId: string) => Promise<void>;
    onDeleteJob: (jobId: string) => void;
    onJobRetried: (jobId: string) => void;
    useModernUI?: boolean;
}

function JobQueueList({ jobs: initialJobs, loading, error, onCancelJob, onDeleteJob, onJobRetried, useModernUI = false }: JobQueueListProps) {
    // Yerel state ve socket dinleyicisi kaldırıldı. Bileşen artık doğrudan props'ları kullanıyor.
    // Her bir JobItem, kendi anlık güncellemelerini JobProgressContext'ten alacak.

    // Modern UI as default
    return (
        <div className="space-y-6">
            {/* Modern Header */}
            <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-xl border border-blue-400/20">
                    <Activity size={24} className="text-blue-300" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">Activity Center</h3>
                    <p className="text-white/60 text-sm">Track your video processing jobs</p>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                    <div className="flex flex-col items-center justify-center text-center space-y-4">
                        <div className="w-12 h-12 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                        <p className="text-white/60">Loading your jobs...</p>
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="bg-red-500/10 backdrop-blur-xl border border-red-400/20 rounded-2xl p-6">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                            <Inbox size={16} className="text-red-400" />
                        </div>
                        <div>
                            <h4 className="font-medium text-red-300">Error Loading Jobs</h4>
                            <p className="text-red-400/80 text-sm">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Jobs List */}
            {!loading && initialJobs && initialJobs.length > 0 && (
                <div className="space-y-4">
                    {initialJobs.map((job) => (
                        <ModernJobItem
                            key={job.id}
                            job={job}
                            onCancelJob={onCancelJob}
                            onDeleteJob={onDeleteJob}
                            onJobRetried={onJobRetried}
                        />
                    ))}
                </div>
            )}

            {/* Empty State */}
            {!loading && initialJobs && initialJobs.length === 0 && !error && (
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                    <div className="text-center space-y-6">
                        {/* Icon */}
                        <div className="flex justify-center">
                            <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-500/20 to-slate-600/20 backdrop-blur-xl border border-slate-400/20">
                                <Sparkles size={48} className="text-slate-300" />
                            </div>
                        </div>
                        
                        {/* Content */}
                        <div className="space-y-3">
                            <h4 className="text-xl font-semibold text-white">
                                No Jobs Created Yet
                            </h4>
                            <p className="text-white/60 max-w-md mx-auto">
                                Paste a Twitch or Kick VOD link from the form on the left to create your first AI-powered highlight clips.
                            </p>
                        </div>
                        
                        {/* Decorative Elements */}
                        <div className="flex justify-center items-center gap-2 pt-4">
                            <div className="w-2 h-2 rounded-full bg-blue-400/40" />
                            <div className="w-2 h-2 rounded-full bg-cyan-400/40" />
                            <div className="w-2 h-2 rounded-full bg-purple-400/40" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default JobQueueList;